//-tech-debt-react-query;
import { DefaultOptions, QueryCache, QueryClient } from '@tanstack/react-query';

/**
 * Pull an HTTP status off a thrown error, whatever shape it arrived in. Our API helpers throw an
 * `ApiError` with a `.status` (see aiApi.ts); other layers attach `.status`/`.statusCode` or embed
 * it in the message ("... status 429"). Returns 0 when no status can be found (network/parse error).
 */
const errorStatus = (error: unknown): number => {
  const e = error as any;
  if (e && typeof e.status === 'number') return e.status;
  if (e && typeof e.statusCode === 'number') return e.statusCode;
  const m = /\b(4\d\d|5\d\d)\b/.exec(String(e?.message ?? ''));
  return m ? Number(m[1]) : 0;
};

/**
 * A client error (400–499) will not succeed on retry — a 429 in particular only deepens the rate
 * limit, and 401/403/404 are terminal. So never retry a 4xx; for everything else (5xx, network)
 * allow at most 2 attempts total. This replaced a blanket `retry: 3`, which hammered `ai/history`
 * with repeated 429s after the first rate-limited response.
 */
const shouldRetry = (failureCount: number, error: unknown): boolean => {
  const status = errorStatus(error);
  if (status >= 400 && status < 500) return false;
  return failureCount < 2;
};

const queryConfig = {
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 60,
  gcTime: 1200 * 60 * 60,
  retry: shouldRetry,
  retryDelay: 2000,
};

const defaultOptions: DefaultOptions = {
  queries: queryConfig,
  mutations: {
    retry: shouldRetry,
    retryDelay: 2000,
  },
};

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error('Query error: ', error, query);
    },
  }),
  defaultOptions: defaultOptions,
});

export {
  queryClient
};